/**
 * P149-W: the shared vocabulary for the Matching Workspace.
 *
 * Ready Pool and Recommendations used to be two pages with two mental models,
 * which is why one of them always looked empty and neither explained the other.
 * They are one pipeline:
 *
 *   Ready Pool -> Recommendations -> ยืนยันโดยนายจ้าง -> Assignments
 *
 * Everything here derives from what the API actually returns. No stage is
 * invented, and where the platform genuinely has nothing yet (no open jobs, no
 * approvals) that is reported as a real state rather than padded with samples.
 */

export const WORKSPACE_TABS = {
  ReadyPool: "ready-pool",
  Recommendations: "recommendations",
  Approved: "approved",
  Assignments: "assignments",
};

// One tab per stepper step, in the same order and with the same words, so the
// two never have to be reconciled by the reader.
// Step 1 is named for the decision it asks for, not for the bucket it reads
// from: an operator standing here is confirming a profile and sending that
// worker into matching, which "Ready Pool" described only to someone who
// already knew the pipeline.
// Step 3 is named for whose decision it is waiting on, not for who made the
// last one: "Final Approval" described the admin's own approval, which by then
// has already happened one step earlier. What the row is actually waiting for
// is the employer, so that is what the tab says.
export const WORKSPACE_TAB_LABELS = {
  [WORKSPACE_TABS.ReadyPool]: "ยืนยันโปรไฟล์",
  [WORKSPACE_TABS.Recommendations]: "Recommendations",
  [WORKSPACE_TABS.Approved]: "ยืนยันโดยนายจ้าง",
  // Step 4 is what happens once the employer has said yes: preparing the
  // worker to start. "Assignments" named the record, not the step, and the
  // record does not exist yet at this point in the pipeline.
  [WORKSPACE_TABS.Assignments]: "Onboard",
};

/**
 * The employer's answer, as the API sends it. Null means they have not
 * answered - never a soft no, and no screen may render it as one.
 */
export const EMPLOYER_DECISIONS = {
  Accepted: "Accepted",
  Rejected: "Rejected",
};

export const EMPLOYER_DECISION_LABELS = {
  [EMPLOYER_DECISIONS.Accepted]: "นายจ้างรับเข้าทำงาน",
  [EMPLOYER_DECISIONS.Rejected]: "นายจ้างไม่รับ",
};

/**
 * Which of the four pipeline states one sent recommendation is in.
 *
 * A rejected row is deliberately NOT a dead end: the worker was never
 * un-readied - approving one recommendation does not touch dbo.WmxWorkers at
 * all - so they are still in the ready pool and can be sent to a different
 * job. That is what "กลับไปขั้นที่ 1" means here, and it is a fact about the
 * data rather than a move this screen performs.
 */
export function employerDecisionOf(recommendation) {
  const decision = recommendation?.employerDecision ?? null;
  if (decision === EMPLOYER_DECISIONS.Accepted || decision === EMPLOYER_DECISIONS.Rejected) {
    return decision;
  }
  return null;
}

/** The stepper reads left to right and never claims a step is done. */
export const WORKSPACE_STEPS = [
  { key: WORKSPACE_TABS.ReadyPool, label: "ยืนยันโปรไฟล์" },
  { key: WORKSPACE_TABS.Recommendations, label: "Recommendations" },
  { key: WORKSPACE_TABS.Approved, label: "ยืนยันโดยนายจ้าง" },
  { key: WORKSPACE_TABS.Assignments, label: "Onboard" },
];

/**
 * Which bucket a worker belongs in.
 *
 * The four groups answer "who can I act on right now" without the reviewer
 * opening another page: the readiness evaluator already computed the blocking
 * facts, so this only reads them - it never re-derives eligibility, which would
 * let the list and the drawer disagree.
 */
export const POOL_GROUPS = {
  Ready: "ready",
  Incomplete: "incomplete",
  AwaitingEkyc: "awaiting-ekyc",
  NeedsProfile: "needs-profile",
};

export const POOL_GROUP_META = {
  [POOL_GROUPS.Ready]: {
    label: "พร้อมแนะนำงาน",
    hint: "ผ่านเกณฑ์ครบ แนะนำเข้างานได้ทันที",
    tone: "success",
  },
  [POOL_GROUPS.AwaitingEkyc]: {
    label: "รอ e-KYC",
    hint: "ยังไม่ผ่านการตรวจ e-KYC จึงยังจับคู่งานไม่ได้",
    tone: "warning",
  },
  [POOL_GROUPS.NeedsProfile]: {
    label: "ต้องเติม Worker Profile",
    hint: "ผ่าน e-KYC แล้ว แต่ยังไม่มีข้อมูลโปรไฟล์ที่ใช้จับคู่งาน",
    tone: "info",
  },
  // The old label read "มีโปรไฟล์แล้ว แต่ยังไม่พร้อม Match", which claimed
  // more than the data supports: the LIST endpoint carries no readiness
  // evaluation, so a worker lands here whenever readiness is simply unknown -
  // and on the live dataset most of them turn out to be fully eligible the
  // moment the detail is read. Calling that "ยังไม่พร้อม" contradicted the
  // confirmation dialog, which is the surface that actually knows.
  [POOL_GROUPS.Incomplete]: {
    label: "รอยืนยันส่งเข้าจับคู่",
    hint: "กด ยืนยันส่งเข้าจับคู่ เพื่อให้ระบบตรวจโปรไฟล์ครั้งสุดท้าย แล้วบอกว่าครบตามเกณฑ์หรือยังขาดข้อไหน",
    tone: "muted",
  },
};

const EKYC_KEYS = new Set(["ekycApproved", "ekyc"]);
const PROFILE_KEYS = new Set([
  "dateOfBirth",
  "passportNumber",
  "passportExpiry",
  "passportDocument",
  "skill",
  "provinces",
  "nationality",
  "language",
]);

/**
 * Classifies one candidate from the readiness facts the API already returns.
 *
 * Order matters: e-KYC gates everything after it, so an unverified worker is
 * reported as awaiting e-KYC even if their profile is also thin - telling an
 * operator to fill in a profile for someone who may yet fail verification
 * wastes the work.
 */
export function classifyCandidate(candidate) {
  // The detail endpoint carries a full readiness evaluation; the list endpoint
  // does not. Prefer the evaluation when it is present, and otherwise classify
  // from the flags the list genuinely returns - grouping on a field that is not
  // in the payload silently dumped every worker into one bucket.
  const readiness = candidate?.readiness ?? null;
  if (readiness?.readyEligibility === true) {
    return POOL_GROUPS.Ready;
  }
  if (readiness?.checklist?.length) {
    const unmet = readiness.checklist.filter((item) => item.blocking && !item.filled);
    if (unmet.some((item) => EKYC_KEYS.has(item.key))) {
      return POOL_GROUPS.AwaitingEkyc;
    }
    if (unmet.length > 0 && unmet.every((item) => PROFILE_KEYS.has(item.key))) {
      return POOL_GROUPS.NeedsProfile;
    }
    return unmet.length === 0 ? POOL_GROUPS.Ready : POOL_GROUPS.Incomplete;
  }

  if (candidate?.readyForMatch === true) {
    return POOL_GROUPS.Ready;
  }
  // WorkerStatus is the only lifecycle signal on a list row. A worker without a
  // profile yet is a different job from one whose profile exists but has not
  // been marked ready.
  const status = candidate?.workerStatus;
  if (status && status !== "ProfileCreated") {
    return POOL_GROUPS.NeedsProfile;
  }
  return POOL_GROUPS.Incomplete;
}

/** Initials for the avatar fallback when a worker has no photo on file. */
export function initialsOf(fullName) {
  const parts = String(fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Passport numbers are identity documents. The workspace only ever needs to
 * know whether one is on file and to let an operator confirm they are looking
 * at the right person, so it shows the last four characters and nothing more.
 */
export function maskPassport(passportNumber) {
  const raw = String(passportNumber ?? "").trim();
  if (!raw) {
    return null;
  }
  if (raw.length <= 4) {
    return "•".repeat(raw.length);
  }
  return `${"•".repeat(Math.min(raw.length - 4, 6))}${raw.slice(-4)}`;
}
